import { ICombo, IComboSlot, IOffer, PickerSelection } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { AvailableRewards, IAppliedRewards } from 'state/loyalty/hooks/types';
import { LoyaltyOffer } from 'state/loyalty/types';
import { ProductMenuObject } from 'state/product-wizard-v2/types';
import { IPricingFunctionFn } from 'types/menu-pricing';

import { computeAvailableRewardSlots } from './price-quantity';

export const computeComboSlotCalories = (comboSlot: IComboSlot) => {
  const options = comboSlot.options || [];

  const noDefaultComboSlotOption = options.every(
    option => !option.defaultAmount || option.defaultAmount === 0
  );

  return options.reduce((sum, comboSlotOption, index) => {
    const comboSlotOptionCalories = comboSlotOption.option?.nutritionWithModifiers?.calories || 0;

    let comboSlotOptionQuantity: number;

    if (noDefaultComboSlotOption) {
      comboSlotOptionQuantity = index === 0 ? 1 : 0;
    } else {
      comboSlotOptionQuantity = comboSlotOption.defaultAmount ?? comboSlotOption.minAmount ?? 0;
    }

    return sum + comboSlotOptionCalories * comboSlotOptionQuantity;
  }, 0);
};

const computeComboCalories = (combo: ICombo) => {
  let totalCalories = 0;

  (combo.options || []).forEach(comboSlot => {
    totalCalories += computeComboSlotCalories(comboSlot);
  });

  if (combo.mainItem) {
    totalCalories += combo.mainItem.nutritionWithModifiers?.calories || 0;
  }

  return totalCalories;
};

export const computeProductCalories = (selectedProduct: PickerSelection) => {
  if (selectedProduct._type === MenuObjectTypes.COMBO) {
    return computeComboCalories(selectedProduct);
  }
  return selectedProduct.nutritionWithModifiers?.calories || 0;
};

export const computeDiscountPrice = ({
  appliedLoyaltyRewards,
  availableLoyaltyRewardsMap,
  cartIdEditing,
  pricingFunction,
  productQuantity,
  rewardBenefitId,
  selectedProduct,
  menuObject,
  stagedCartPoints,
  isFromCart,
  preventRewardStacking,
  rewardRedemptionLimit = 1,
}: {
  appliedLoyaltyRewards: IAppliedRewards;
  availableLoyaltyRewardsMap: AvailableRewards;
  cartIdEditing?: string;
  pricingFunction: IPricingFunctionFn;
  productQuantity: number;
  rewardBenefitId?: string;
  selectedProduct: PickerSelection;
  menuObject?: ProductMenuObject;
  stagedCartPoints?: number;
  isFromCart?: boolean;
  preventRewardStacking?: boolean;
  /** User-level cap on total reward applications (`LoyaltyUser.rewardRedemptionLimit`). */
  rewardRedemptionLimit?: number;
}) => {
  // RewardBenefitId has values when coming from Rewards (is applying a single Reward).
  // CartIdEditing has values when editing a CartEntry. That CartEntry might have Rewards applied.
  // If both are empty, user is coming from Menu, and there is no discount to apply
  const isDiscountEnabledForCurrentRoute = rewardBenefitId || cartIdEditing;
  // If the item we are pricing is not applicable for Rewards, there is no discount to apply
  const reward =
    availableLoyaltyRewardsMap[selectedProduct._id] ||
    availableLoyaltyRewardsMap[menuObject?._id || ''];

  if (!isDiscountEnabledForCurrentRoute || !reward) {
    return 0;
  }

  const price = pricingFunction({ item: selectedProduct, quantity: productQuantity });
  const individualItemPrice = (price || 0) / productQuantity;

  let discountPrice = 0;
  const timesRewardApplied = cartIdEditing ? appliedLoyaltyRewards[cartIdEditing]?.timesApplied : 0;

  // Check if a discount should be applied based on the current context
  const shouldApplyDiscount = (isFromCart && timesRewardApplied > 0) || rewardBenefitId;

  if (!shouldApplyDiscount) {
    return discountPrice;
  }

  const { pointCost } = reward;
  const usedPointsInCart = timesRewardApplied * pointCost;

  /**
   * When the cart is at full reward capacity and we are adding a new reward (not editing),
   * the legacy add flow (`use-product-cart#processRewardRedemption`) evicts the oldest
   * applied reward to free a slot. Reserve that slot here AND restore the evicted reward's
   * points so the price preview reflects the post-swap state instead of showing full price.
   */
  const { availableRewardSlots, reservedEvictionPointCost } = computeAvailableRewardSlots({
    appliedLoyaltyRewards,
    rewardRedemptionLimit,
    cartIdEditing,
    rewardBenefitId,
    preventRewardStacking: !!preventRewardStacking,
    incomingRewardId: reward.id,
    incomingLimitPerOrder: reward.limitPerOrder,
  });
  const effectiveStagedCartPoints = (stagedCartPoints || 0) + reservedEvictionPointCost;

  const calculateTotalPointCost = (quantity: number) => pointCost * quantity - usedPointsInCart;

  const hasSufficientPoints = (quantity: number) =>
    effectiveStagedCartPoints >= calculateTotalPointCost(quantity);

  // Function to calculate discount price.
  // Since we need to re-calculate the discount price when the user changes the quantity of the item,
  // we need to add the times the reward has been applied to the max redeemable items.
  const calculateDiscountPrice = (quantity: number, maxRedeemable: number) => {
    return individualItemPrice * Math.min(maxRedeemable + timesRewardApplied, quantity);
  };

  const productQuantityForDiscount = preventRewardStacking
    ? Math.min(availableRewardSlots, productQuantity)
    : productQuantity;

  if (hasSufficientPoints(productQuantityForDiscount)) {
    // If there are enough points, apply discount to all items
    discountPrice = individualItemPrice * productQuantityForDiscount;
  } else {
    // Calculate the maximum redeemable items based on available points
    const maxRedeemableItems = Math.floor(effectiveStagedCartPoints / pointCost);
    // Apply discount only to the maximum redeemable items
    discountPrice = calculateDiscountPrice(productQuantityForDiscount, maxRedeemableItems);
  }

  return discountPrice;
};

export const getOfferVendorConfigForPricing = (offer: IOffer | LoyaltyOffer | null) => {
  return offer?.marketPrice?.vendorConfigs || offer?.vendorConfigs;
};
